import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import DetailWrapper from "../../detail/DetailWrapper";
import withMeta from '../../utils/withMeta';
import { parseValue, processAfter, processBefore, isInvalid as isInvalidHelper } from '../../utils/helper';
import Button from "../../buttons/Button";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import LoadingPanel from "../../LoadingPanel";
import { useDidMount } from '../../utils/hooks';

const frmPrimaryField = "IUz";

const UzDetail = (props) => {
  const { refresh, setUz, uz, db, meta } = props;

  const [state, setState] = useState({
    isLoading: true,
    schema: [],
    ui: [],
    detailData: null,
    isOpenConfirmDialog: false
  });

  const detailRef = useRef(null);

  const getSchema = async () => {
    try {
      const res = await meta.getSchema(frmPrimaryField, db);
      const schema = res.Detail;
      const ui = res.Args?.ui ?? [];
      let detailData = {};
      for (const x of schema) {
        detailData[x.field] = await getValue(x);
      }

      setState((prevState) => ({ ...prevState, schema, ui, detailData: prevState.detailData ? prevState.detailData : detailData, isLoading: false }));
    } catch (error) {
      setState({ ...state, isLoading: false });
    }
  };

  useDidMount(() => {
    getSchema();
  })

  useEffect(() => {
    setState((prevState) => ({ ...prevState, detailData: uz }))
  }, [uz]);

  useEffect(() => {
    const openConfirmDialog = () => {
      setState((prevState) => ({ ...prevState, isOpenConfirmDialog: true }));
    };

    if (state.detailData && refresh) {
      if (_.isEqual(uz, state.detailData)) {
        onNext();
      } else {
        openConfirmDialog();
      }
    }
    // eslint-disable-next-line
  }, [refresh]);

  const getList = async (filter, sort, page, primaryField) => {
    try {
      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: true, data: [], total: 0 }
      }));

      const res = await meta.getList(primaryField, filter, sort, page, db);

      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: false, data: res.Data.Items, total: res.Data.TotalRecords }
      }));

      props.getListSuccess(res);
    } catch (error) {
      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: false, data: [], total: 0 }
      }));

      props.getListFailure(error);
    }
  };

  const onCreate = async (d) => {
    let detailData = processBefore(state.schema, d);
    detailData[frmPrimaryField] = 0;

    try {
      setState({ ...state, isLoading: true });

      const res = await meta.create(frmPrimaryField, detailData, db);
      res.Data = processAfter(state.schema, res.Data);

      setState({ ...state, detailData: res.Data, isLoading: false });
      props.createSuccess(res);

      setUz(res.Data);
    } catch (error) {
      setState({ ...state, isLoading: false });
      props.createFailure(error);
    }
  };

  const onUpdate = async (id, d) => {
    if (_.isEqual(uz, d)) {
      setUz(uz);
      return;
    }

    let detailData = processBefore(state.schema, d);
    try {
      setState({ ...state, isLoading: true });

      const res = await meta.update(frmPrimaryField, id, detailData, db);
      res.Data = processAfter(state.schema, res.Data);

      setState({ ...state, detailData: res.Data, isLoading: false });

      props.updateSuccess(res);
      setUz(res.Data);
    } catch (error) {
      setState({ ...state, isLoading: false });
      props.updateFailure(error);
    }
  };

  const getPickerValue = async (primaryField, defaultValue) => {
    const id = parseValue(defaultValue, 'container');
    if (id === parseInt(id, 10)) {
      const res = await meta.get(primaryField, id, db);
      if (res && res.Data) {
        return res.Data;
      }
    }
    return null;
  };

  const getValue = async (x) => {
    const { defaultValue } = x;

    switch (x.type) {
      case 'container':
        return defaultValue ? await getPickerValue(x.primaryField, defaultValue) : null;
      case 'boolean':
        return parseValue(defaultValue) || false;
      default:
        return parseValue(defaultValue, x.type);
    }
  };

  const onChange = (name, value) => {
    setState(prevState => ({
      ...prevState,
      detailData: { ...prevState.detailData, [name]: value }
    }));
  };

  const onNext = () => {
    detailRef.current.handleSubmit(new Event('submit'));
  };


  const onCloseConfirmDialog = () => {
    setState((prevState) => ({ ...prevState, isOpenConfirmDialog: false }));
  };

  const onConfirmDialog = async () => {
    onCloseConfirmDialog();

    if (isDefaultValid()) {
      setUz(uz);
      return;
    }

    let detailData = {};
    for (const x of state.schema) {
      detailData[x.field] = await getValue(x);
    }
    if (uz) {
      detailData = uz;
    }

    setState((prevState) => ({ ...prevState, detailData }), onNext);
  };

  const isDefaultValid = () => {
    if (!uz) {
      return false;
    }

    let submit = true;
    for (let field of state.schema) {
      const value = uz[field.field];

      if (isInvalid(field, value)) {
        return false;
      }
    };
    return submit;
  };

  const isInvalid = (field, value) => {
    if (field.disabled) {
      return false;
    }

    const inEdit = uz["IUz"] !== null;
    return isInvalidHelper(field, value, inEdit);
  };

  if (state.isLoading) {
    return <LoadingPanel isLoading={state.isLoading} />;
  }

  return (
    <>
      <DetailWrapper
        ref={detailRef}
        primaryField={frmPrimaryField}
        schema={state.schema}
        data={state.detailData}
        ui={state.ui}
        parentProps={{ ...state, getList }}
        onChange={onChange}
        onCreate={onCreate}
        isLoading={state.isLoading}
        allData={[]}
        onGet={() => { }}
        onUpdate={onUpdate}
        onSubmit={() => { }}
        toolbarHidden={true}
        withMeta
      />
      <Button
        buttonWrapperClassName={"next-button"}
        label={"Zaevidovať používateľa"}
        onClick={onNext}
        primary
      />
      <ConfirmDialog
        isOpen={state.isOpenConfirmDialog}
        onClose={onCloseConfirmDialog}
        onConfirm={onConfirmDialog}
        text={"Máte rozpracovaný záznam. Chcete zahodiť zmeny?"}
        confirmText="Áno"
        cancelText="Nie"
      />
    </>
  );

}

UzDetail.propTypes = {
  refresh: PropTypes.number,
  setUz: PropTypes.func.isRequired,
  uz: PropTypes.object,
  db: PropTypes.string
}

const mapStateToProps = () => ({
})

const mapDispatchToProps = dispatch => ({
  getListSuccess: res => dispatch({ type: 'GET_LIST_SUCCESS', res }),
  getListFailure: error => dispatch({ type: 'GET_LIST_FAILURE', error }),
  createSuccess: res => dispatch({ type: 'CREATE_SUCCESS', res }),
  createFailure: error => dispatch({ type: 'CREATE_FAILURE', error }),
  updateSuccess: res => dispatch({ type: 'UPDATE_SUCCESS', res }),
  updateFailure: error => dispatch({ type: 'UPDATE_FAILURE', error })
})

export default withMeta(connect(
  mapStateToProps,
  mapDispatchToProps
)(UzDetail));